// Install html-to-image if not present: npm install html-to-image
import React, { forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom/client';
import GridCanvasRenderer from './GridCanvasRenderer';
import * as htmlToImage from 'html-to-image';
import { getImage, cacheImage } from '../ImageCache';

const LOGO_URL = '/images/StoryWeaverLogo.svg';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'storyweaver.app';
const CELL_SIZE = 122;
const ZOOM = 4;
const CARD_SIZE = CELL_SIZE * ZOOM;

export function sanitizeFilename(name) {
  return (name || 'storyweaver-canvas').replace(/[\\/:*?\"<>|.]/g, '').replace(/^\s+|\s+$/g, '');
}

// Convert a cached HTMLImageElement to a data URL (eliminates future fetches by html-to-image)
function imageToDataUrl(img) {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    }
  } catch (e) {
    // CORS or other issues — fall back to original src
  }
  return null;
}

// Replace all img src AND CSS background-image URLs in the container with data URLs
// from ImageCache. This is what stops html-to-image from making 900+ fetches:
// every image reference becomes an embedded data URL, so there's nothing to fetch.
async function embedCachedImages(container) {
  const pending = [];

  // 1. Replace <img> src attributes
  const imgs = container.querySelectorAll('img');
  for (const img of imgs) {
    const src = img.getAttribute('src');
    if (!src) continue;
    const cached = getImage(src);
    if (cached && cached.complete && cached.naturalWidth > 0) {
      const dataUrl = imageToDataUrl(cached);
      if (dataUrl) {
        pending.push(new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
          img.src = dataUrl;
        }));
      }
    }
  }

  // 2. Replace CSS background-image URLs on all elements
  const allEls = container.querySelectorAll('*');
  for (const el of allEls) {
    const bgImage = el.style.backgroundImage;
    if (!bgImage || bgImage === 'none') continue;
    // Extract URL from url("...") or url(...)
    const match = bgImage.match(/url\(['"]?([^'")]+)['"]?\)/);
    if (!match) continue;
    const url = match[1];
    if (url.startsWith('data:')) continue; // already embedded
    const cached = getImage(url);
    if (cached && cached.complete && cached.naturalWidth > 0) {
      const dataUrl = imageToDataUrl(cached);
      if (dataUrl) {
        el.style.backgroundImage = `url(${dataUrl})`;
      }
    }
  }

  if (pending.length) await Promise.all(pending);
}

const CanvasExportButton = forwardRef(function CanvasExportButton({ cards, relations, canvas, symbols, selectedTextOption, language, decks, cardTypes }, ref) {
  const exportCards = cards.map(card => ({
    ...card,
    cell_x: card.pos_x,
    cell_y: card.pos_y,
  }));

  const handleExportPNG = async () => {
    if (!cards.length) return;

    // setIsCreating is managed by the parent (Canvas.js) via isCreatingImage state
    try {
      // --- getUsedBounds ---
      function getUsedBounds(cards, relations) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const card of cards) {
          minX = Math.min(minX, card.pos_x);
          minY = Math.min(minY, card.pos_y);
          maxX = Math.max(maxX, card.pos_x + 1);
          maxY = Math.max(maxY, card.pos_y + 1);
        }
        for (const rel of relations) {
          if (rel.path && rel.path.length) {
            rel.path.forEach(pt => {
              minX = Math.min(minX, pt.cellX);
              minY = Math.min(minY, pt.cellY);
              maxX = Math.max(maxX, pt.cellX + 1);
              maxY = Math.max(maxY, pt.cellY + 1);
            });
          }
        }
        const PADDING = 0.1;
        minX -= PADDING; minY -= PADDING;
        maxX += PADDING; maxY += PADDING;
        return { minX, minY, maxX, maxY };
      }
      const { minX, minY, maxX, maxY } = getUsedBounds(cards, relations);
      const gridWidth = maxX - minX;
      const gridHeight = maxY - minY;
      const width = gridWidth * CARD_SIZE;
      const height = gridHeight * CARD_SIZE;
      const gridOffset = {
        x: -(((minX + maxX) / 2) * CARD_SIZE),
        y: -(((minY + maxY) / 2) * CARD_SIZE)
      };
      const TITLE_HEIGHT = 160;
      const FOOTER_HEIGHT = 120;

      // --- 1. Pre-load all unique image URLs into browser + ImageCache ---
      const imageUrls = new Set();
      symbols.forEach(s => { if (s.symbol) imageUrls.add(s.symbol); });
      exportCards.forEach(card => {
        if (canvas?.showImages && card.custom_background) imageUrls.add(card.custom_background);
        if (card.default_background) imageUrls.add(card.default_background);
      });
      if (imageUrls.size > 0) {
        await Promise.all(Array.from(imageUrls).map(url => new Promise(resolve => {
          const img = new Image();
          img.onload = () => { cacheImage(url, img); resolve(); };
          img.onerror = resolve;
          img.src = url;
        })));
      }

      // --- 2. Create hidden DOM node and render GridCanvasRenderer ---
      const container = document.createElement('div');
      container.style.cssText = `position:fixed;left:-9999px;top:-9999px;width:${width}px;height:${height}px;pointer-events:none;opacity:0;background:transparent`;
      document.body.appendChild(container);

      let root = ReactDOM.createRoot(container);
      root.render(<div style={{ width, height, background: 'transparent' }}>
        <GridCanvasRenderer
          cards={exportCards} relations={relations} selectedCardId={null}
          selectedTextOption={selectedTextOption} gridOffset={gridOffset} zoom={ZOOM}
          language={language} symbols={symbols} decks={decks} cardTypes={cardTypes}
          relationPreviewPath={null} relationPreviewColor={'#ff0000'}
          onSelectCard={null} onSelectTextOption={null} onMoveCard={null}
          onGridOffsetChange={null} onZoomChange={null} onContainerSizeChange={null}
          onRelationClick={null} selectedRelation={null}
          style={{ background: 'transparent' }} disableCanvasReferences={true}
          showImages={canvas?.showImages} imageCache={null} isExportMode={true}
        />
      </div>);

      // Wait for React to flush
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const check = () => {
          if (container.firstChild) return resolve();
          if (++attempts > 50) return reject(new Error('Timeout waiting for React render'));
          setTimeout(check, 50);
        };
        check();
      });

      // --- 3. Replace all image refs (img src + CSS background-image) with data URLs ---
      await embedCachedImages(container);

      // --- 4. Capture PNG via html-to-image (no fetches — images are data URLs now) ---
      let dataUrl;
      try {
        dataUrl = await htmlToImage.toPng(container.firstChild, { backgroundColor: 'rgba(0,0,0,0)', pixelRatio: 1, skipFonts: true });
      } catch (e) {
        alert(`Failed to export image: ${e.message}`);
        root.unmount();
        document.body.removeChild(container);
        return;
      }

      // --- 5. Draw title, PNG, footer to final canvas ---
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = width;
      finalCanvas.height = height + TITLE_HEIGHT + FOOTER_HEIGHT;
      const ctx = finalCanvas.getContext('2d');
      ctx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);

      // Title
      ctx.save();
      ctx.font = 'bold 80px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#222';
      ctx.globalAlpha = 1;
      ctx.fillText(canvas?.name || 'StoryWeaver Canvas', width / 2, 100);
      ctx.restore();

      // PNG image
      await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => { ctx.drawImage(img, 0, TITLE_HEIGHT, width, height); resolve(); };
        img.onerror = reject;
        img.src = dataUrl;
      });

      // Footer
      const footerY = height + TITLE_HEIGHT + 10;
      ctx.save();
      ctx.font = '40px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillStyle = '#222';
      ctx.globalAlpha = 1;
      ctx.fillText('Created with StoryWeaver', width - 90, footerY + 10);

      // Logo
      await new Promise((resolve, reject) => {
        const logoImg = new window.Image();
        logoImg.onload = () => { ctx.drawImage(logoImg, width - 80, footerY - 40, 80, 80); resolve(); };
        logoImg.onerror = reject;
        logoImg.src = LOGO_URL;
      });

      ctx.font = '16px sans-serif';
      ctx.fillStyle = '#4a90e2';
      ctx.textAlign = 'right';
      ctx.fillText(SITE_URL, width - 90, footerY + 36);
      ctx.restore();

      // --- 6. Download via blob URL (async, non-blocking) ---
      const blob = await new Promise(resolve => finalCanvas.toBlob(resolve, 'image/png'));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = sanitizeFilename(canvas?.name) + '.png';
      a.click();
      // Revoke after a delay to ensure the download has started
      setTimeout(() => URL.revokeObjectURL(url), 15000);

      // --- 7. Clean up ---
      root.unmount();
      document.body.removeChild(container);
    } catch (e) {
      console.error('Export failed:', e);
      alert(`Failed to export image: ${e.message}`);
    }
  };

  useImperativeHandle(ref, () => ({ handleExportPNG }));
  return null;
});

export default CanvasExportButton;
